import { useEffect, useState } from "react";

import clsx from "clsx";
import { Controller, type Control, type FieldErrors } from "react-hook-form";

import { useMmmAnnotations } from "@features/mmm/hooks/use-mmm-annotations";
import { Select } from "@shared/selects";
import { createSearchHighlightedLabel } from "@shared/selects/utils";

import { PromotionCalendarCampaignTypeModal } from "./promotion-calendar-campaign-type-modal";
import { type NewPromotionCalendar } from "./promotion-calendar-modal";

const CREATE_NEW_CAMPAIGN_TYPE_ID = "create-a-new-campaign-type";

interface Props {
  currentPromotionCalendar: NewPromotionCalendar;
  errors: FieldErrors<NewPromotionCalendar>;
  control: Control<NewPromotionCalendar>;
}

export const PromotionCalendarCampaignType = ({
  currentPromotionCalendar,
  errors,
  control,
}: Props) => {
  const { campaignTypes, isCampaignTypesLoading } = useMmmAnnotations();

  const [currentCampaignTypes, setCurrentCampaignTypes] = useState<string[]>(
    [],
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setCurrentCampaignTypes((prevCampaignTypes) => {
      const campaignTypesSet = new Set([
        ...campaignTypes,
        ...prevCampaignTypes,
      ]);
      return Array.from(campaignTypesSet);
    });
  }, [campaignTypes]);

  const addNewCampaignType = (newCampaignType: string) =>
    setCurrentCampaignTypes((prevCampaignTypes) => {
      const campaignTypesSet = new Set([...prevCampaignTypes, newCampaignType]);
      return Array.from(campaignTypesSet);
    });

  const hasCampaignType = "campaignType" in currentPromotionCalendar;

  if (!hasCampaignType) {
    return null;
  }

  return (
    <div>
      <Controller
        control={control}
        name="campaignType"
        rules={{ required: "Campaign type is required" }}
        render={({ field }) => {
          const onChange = (campaignType: string | null | undefined) => {
            if (campaignType === CREATE_NEW_CAMPAIGN_TYPE_ID) {
              setIsModalOpen(true);
            } else {
              field.onChange(campaignType ?? null);
            }
          };
          return (
            <div>
              <Select
                value={field.value}
                comboboxRefSetter={field.ref}
                onChange={onChange}
                name="campaignType"
                label="CAMPAIGN TYPE"
                isLoading={isCampaignTypesLoading}
                errors={errors}
                options={[
                  {
                    label: "Create a new campaign type",
                    value: CREATE_NEW_CAMPAIGN_TYPE_ID,
                  },
                  ...currentCampaignTypes.map((value) => ({
                    value,
                    label: value,
                  })),
                ]}
                menuItemRender={({
                  option,
                  active,
                  selected,
                  search,
                  onChange,
                }) => {
                  return (
                    <div
                      className={clsx(
                        (active || selected) && !option.disabled
                          ? "bg-cosmic-3 font-semibold"
                          : "hover:bg-cosmic-4",
                        option.disabled && "bg-gray-3 pointer-events-none",
                        "px-4 py-2 cursor-pointer text-v2-primary font-normal",
                      )}
                      onClick={() => {
                        onChange(option.value);
                      }}
                    >
                      {option.value === CREATE_NEW_CAMPAIGN_TYPE_ID ? (
                        <div className="text-cosmic flex items-center gap-2">
                          <i className="fa-regular fa-circle-plus" />
                          {option.label}
                        </div>
                      ) : (
                        <div
                          dangerouslySetInnerHTML={createSearchHighlightedLabel(
                            option.label,
                            search,
                          )}
                        ></div>
                      )}
                    </div>
                  );
                }}
              />
              <PromotionCalendarCampaignTypeModal
                isOpen={isModalOpen}
                onAdd={(value) => {
                  addNewCampaignType(value);
                  field.onChange(value);
                  setIsModalOpen(false);
                }}
                onClose={() => setIsModalOpen(false)}
              />
            </div>
          );
        }}
      />
    </div>
  );
};
